import { useContext, useState, useCallback, useEffect } from 'react';
import { Database, Plus, UserPlus, Users, ShieldCheck, ShieldX } from 'lucide-react';
import { GlobalContext } from '../../context/GlobalContext';
import { unsafeAPI } from '../../hooks/api';
import Table from '../../components/Table';
import { ConfirmInputDialog, type ConfirmInputDialogProps } from '../../components/ConfirmInputDialog';
import { Dialog } from '../../components/Dialog';
import { Button } from '../../shadcn/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Notice from '../../components/Notice';
import { ContainerSpinner } from '../../components/Spinner';
import { TabBar } from '../../components/TabBar';
import { API_BASE } from '../../utils/constants';
import ApproveProviderDialog from './components/ApproveProviderDialog';

const TYPE_LABELS: Record<string, string> = {
	bank_account: 'Cuenta bancaria',
	identification_number: 'Número de identificación',
	note: 'Nota'
};

const DIRECTION_LABELS: Record<string, string> = {
	egreso: 'Egreso',
	ingreso: 'Ingreso'
};

const DEFAULT_DIRECTION = 'egreso';
const NO_SECONDARY_CATEGORY = '__none__';

type SelectOption = { label: string; value: string };
type CategoriesByDirection = Record<string, SelectOption[]>;
type MappingValues = { direction: string; type: string; category: string; value: string };

function toSelectOptions(values: string[] = [], labels?: Record<string, string>) {
	return values.map((v) => ({ label: labels?.[v] || v.charAt(0).toUpperCase() + v.slice(1), value: v }));
}

const ID_NUMBER_REGEX = /^[JVjv]-\d+$/;

function validateMapping(type: string | boolean, value: string | boolean): string | null {
	if (type === 'identification_number') {
		if (!ID_NUMBER_REGEX.test(String(value))) {
			return 'El número de identificación debe tener el formato J-NÚMERO o V-NÚMERO (ej: J-123456789)';
		}
	}
	return null;
}

function normalizeValue(type: string | boolean, value: string | boolean): string {
	const v = String(value).trim();
	if (type === 'identification_number') return v.charAt(0).toUpperCase() + v.slice(1);
	return v;
}

const ACCOUNT_INTERPOLATION = [
	{ after: 4, char: '-' },
	{ after: 8, char: '-' },
	{ after: 12, char: '-' },
	{ after: 16, char: '-' },
	{ after: 20, char: '-' }
];

function ProviderDialog({
	onConfirm,
	onClose,
	categories,
	isAdmin
}: {
	onConfirm: (values: {
		name: string;
		identification_number: string;
		account_number: string;
		category: string;
		secondary_category: string;
		manual_payment_limit_usd?: number | null;
	}) => Promise<true | string>;
	onClose: () => void;
	isAdmin: boolean;
	categories: { label: string; value: string }[];
}) {
	const [name, setName] = useState('');
	const [identificationNumber, setIdentificationNumber] = useState('');
	const [accountNumber, setAccountNumber] = useState('');
	const [category, setCategory] = useState('');
	const [secondaryCategory, setSecondaryCategory] = useState('');
	const [manualLimit, setManualLimit] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const secondaryCategoryOptions = [
		{ label: 'Sin categoría secundaria', value: NO_SECONDARY_CATEGORY },
		...categories.filter((option) => option.value !== category)
	];

	const handleCategoryChange = (value: string | boolean | null) => {
		const nextCategory = typeof value === 'boolean' ? '' : value || '';
		setCategory(nextCategory);
		if (secondaryCategory === nextCategory) setSecondaryCategory('');
	};

	const handleSubmit = async () => {
		setLoading(true);
		setError(null);

		if (!name.trim()) {
			setError('El nombre es requerido');
			setLoading(false);
			return;
		}
		if (!ID_NUMBER_REGEX.test(identificationNumber)) {
			setError('El RIF debe tener el formato J-NÚMERO o V-NÚMERO (ej: J-123456789)');
			setLoading(false);
			return;
		}
		if (accountNumber.replace(/\D/g, '').length < 16) {
			setError('El número de cuenta debe tener al menos 16 dígitos');
			setLoading(false);
			return;
		}
		if (!category) {
			setError('La categoría es requerida');
			setLoading(false);
			return;
		}
		if (secondaryCategory && secondaryCategory === category) {
			setError('La categoría secundaria debe ser distinta a la principal');
			setLoading(false);
			return;
		}

		if (isAdmin && manualLimit.trim() && (!Number.isFinite(Number(manualLimit)) || Number(manualLimit) <= 0)) {
			setError('El límite por pago en USD debe ser un número positivo');
			setLoading(false);
			return;
		}
		try {
			const result = await onConfirm({
				name: name.trim(),
				identification_number: identificationNumber.charAt(0).toUpperCase() + identificationNumber.slice(1),
				account_number: accountNumber.replace(/\D/g, ''),
				category,
				secondary_category: secondaryCategory,
				...(isAdmin ? { manual_payment_limit_usd: manualLimit.trim() ? Number(manualLimit) : null } : {})
			});
			if (result === true) {
				onClose();
			} else {
				setError(result);
			}
		} catch {
			setError('Ha ocurrido un error inesperado');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog title="Crear Proveedor" onClose={onClose} closable={false} className="min-w-[min(480px,90vw)]">
			<form
				className="flex flex-col gap-4 pt-1"
				onSubmit={(e) => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				{isAdmin && (
					<Input
						name="manual_payment_limit_usd"
						floatingLabel="Límite por pago en USD (opcional)"
						placeholder="Déjalo vacío para usar el límite general"
						onChange={setManualLimit}
					/>
				)}
				<Input name="name" placeholder="Nombre del proveedor" floatingLabel="Nombre" onChange={setName} />
				<Input
					name="identification_number"
					placeholder="J-123456789 o V-123456789"
					floatingLabel="RIF / Cédula"
					onChange={setIdentificationNumber}
				/>
				<Input
					name="account_number"
					placeholder="0191-0001-2345-6789-0123"
					floatingLabel="Número de cuenta"
					allowedChars="numbers_only"
					interpolation={ACCOUNT_INTERPOLATION}
					onChange={setAccountNumber}
				/>
				<Select options={categories} floatingLabel="Categoría" onSelectedChange={handleCategoryChange} />
				<Select
					key={category || 'secondary-category'}
					options={secondaryCategoryOptions}
					defaultValue={secondaryCategory || undefined}
					floatingLabel="Categoría secundaria (opcional)"
					onSelectedChange={(value) =>
						setSecondaryCategory(typeof value === 'string' && value !== NO_SECONDARY_CATEGORY ? value : '')
					}
				/>

				{error && <Notice message={error} variant="error" />}

				<div className="mt-2 flex justify-end gap-3">
					<Button disabled={loading} variant="secondary" onClick={onClose} className="w-fit">
						Cancelar
					</Button>
					<Button type="submit" variant="primary" loading={loading} className="w-fit">
						Confirmar
					</Button>
				</div>
			</form>
		</Dialog>
	);
}

function MappingDialog({
	title,
	initialValues,
	validTypes,
	validDirections,
	categoriesByDirection,
	onConfirm,
	onClose
}: {
	title: string;
	initialValues?: Partial<MappingValues>;
	validTypes: SelectOption[];
	validDirections: SelectOption[];
	categoriesByDirection: CategoriesByDirection;
	onConfirm: (values: MappingValues) => Promise<true | string>;
	onClose: () => void;
}) {
	const [direction, setDirection] = useState(initialValues?.direction || DEFAULT_DIRECTION);
	const [type, setType] = useState(initialValues?.type || '');
	const [category, setCategory] = useState(initialValues?.category || '');
	const [value, setValue] = useState(initialValues?.value || '');
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const categoryOptions = categoriesByDirection[direction] || categoriesByDirection[DEFAULT_DIRECTION] || [];

	const handleDirectionChange = (selected: string | boolean | null) => {
		const nextDirection = typeof selected === 'string' ? selected : DEFAULT_DIRECTION;
		setDirection(nextDirection);
		const nextCategories = categoriesByDirection[nextDirection] || [];
		if (!nextCategories.some((option) => option.value === category)) setCategory('');
	};

	const handleSubmit = async () => {
		setLoading(true);
		setError(null);

		if (!direction || !type || !category || !value.trim()) {
			setError('Por favor, completa todos los campos');
			setLoading(false);
			return;
		}

		const validationError = validateMapping(type, value);
		if (validationError) {
			setError(validationError);
			setLoading(false);
			return;
		}

		try {
			const result = await onConfirm({ direction, type, category, value: normalizeValue(type, value) });
			if (result === true) {
				onClose();
			} else {
				setError(result);
			}
		} catch {
			setError('Ha ocurrido un error inesperado');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog title={title} onClose={onClose} closable={false} className="min-w-[min(480px,90vw)]">
			<form
				className="flex flex-col gap-4 pt-1"
				onSubmit={(e) => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				<Select options={validDirections} defaultValue={direction} floatingLabel="Movimiento" onSelectedChange={handleDirectionChange} />
				<Select
					options={validTypes}
					defaultValue={type || undefined}
					floatingLabel="Tipo"
					onSelectedChange={(selected) => setType(typeof selected === 'string' ? selected : '')}
				/>
				<Select
					key={direction}
					options={categoryOptions}
					defaultValue={category || undefined}
					floatingLabel="Categoría"
					onSelectedChange={(selected) => setCategory(typeof selected === 'string' ? selected : '')}
				/>
				<Input
					name="value"
					placeholder="Cuenta bancaria, RIF, nota..."
					floatingLabel="Valor"
					value={value}
					onChange={setValue}
				/>

				{error && <Notice message={error} variant="error" />}

				<div className="mt-2 flex justify-end gap-3">
					<Button disabled={loading} variant="secondary" onClick={onClose} className="w-fit">
						Cancelar
					</Button>
					<Button type="submit" variant="primary" loading={loading} className="w-fit">
						Confirmar
					</Button>
				</div>
			</form>
		</Dialog>
	);
}

const PAGE_TABS = [
	{ id: 'mappings', label: 'Mappings', icon: Database },
	{ id: 'proveedores', label: 'Proveedores', icon: Users }
];

export default function BankAccountMappings() {
	const { userData, internalCountry } = useContext(GlobalContext);
	const isAdmin = userData?.access_type === 'admin';
	const [activeTab, setActiveTab] = useState('mappings');
	const [tableKey, setTableKey] = useState(Math.random());
	const [proveedoresTableKey, setProveedoresTableKey] = useState(Math.random());
	const [mappingDialogRow, setMappingDialogRow] = useState<{ [key: string]: string | number } | null | 'create'>(null);
	const [editDialogProps, setEditDialogProps] = useState<ConfirmInputDialogProps | null>(null);
	const [providerDialogOpen, setProviderDialogOpen] = useState(false);
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
	const [selectedProviders, setSelectedProviders] = useState<{ [key: string]: string | number }[]>([]);
	const [approveAction, setApproveAction] = useState<'approve' | 'reject' | null>(null);

	const [validTypes, setValidTypes] = useState<SelectOption[]>([]);
	const [validDirections, setValidDirections] = useState<SelectOption[]>(toSelectOptions([DEFAULT_DIRECTION], DIRECTION_LABELS));
	const [validCategories, setValidCategories] = useState<SelectOption[]>([]);
	const [validMappingCategories, setValidMappingCategories] = useState<SelectOption[]>([]);
	const [categoriesByDirection, setCategoriesByDirection] = useState<CategoriesByDirection>({});
	const [optionsLoaded, setOptionsLoaded] = useState(false);

	useEffect(() => {
		(async () => {
			const res = await unsafeAPI('GET', `${API_BASE}/bank_account_mappings/`, null, userData, internalCountry.get());
			if (res?.success) {
				const directions = res.valid_directions?.length ? res.valid_directions : [DEFAULT_DIRECTION];
				const categoriesMap = res.valid_categories_by_direction || { [DEFAULT_DIRECTION]: res.valid_categories || [] };
				const normalizedCategoriesByDirection = Object.fromEntries(
					Object.entries(categoriesMap).map(([direction, categories]) => [direction, toSelectOptions(categories as string[])])
				);

				setValidTypes(toSelectOptions(res.valid_types, TYPE_LABELS));
				setValidDirections(toSelectOptions(directions, DIRECTION_LABELS));
				const allMappingCategories = Object.values(normalizedCategoriesByDirection).flat();
				const uniqueMappingCategories = Array.from(new Map(allMappingCategories.map((option) => [option.value, option])).values());

				setCategoriesByDirection(normalizedCategoriesByDirection);
				setValidCategories(normalizedCategoriesByDirection[DEFAULT_DIRECTION] || toSelectOptions(res.valid_categories));
				setValidMappingCategories(uniqueMappingCategories);
			}
			setOptionsLoaded(true);
		})();
	}, [userData, internalCountry]);

	const refreshTable = () => {
		setTableKey(Math.random());
		setProveedoresTableKey(Math.random());
	};

	const showNotice = (message: string, variant: 'success' | 'error') => {
		setNotice({ message, variant });
		setTimeout(() => setNotice(null), 3000);
	};

	const findMappingIndex = useCallback(
		async (type: string, value: string, direction: string): Promise<number> => {
			const response = await unsafeAPI(
				'POST',
				`${API_BASE}/tables/bank_account_mappings/`,
				{
					master_store_email: userData?.master_store?.id,
					batch_size: 99999,
					last_object_id: null
				},
				userData,
				internalCountry.get()
			);
			if (!response?.rows) return -1;
			return response.rows.findIndex((row: string[]) => row[0] === type && row[2] === value && (row[4] || DEFAULT_DIRECTION) === direction);
		},
		[userData, internalCountry]
	);

	const handleCreate = () => setMappingDialogRow('create');

	const handleConfirmMapping = async (values: MappingValues, row?: { [key: string]: string | number }): Promise<true | string> => {
		const method = row ? 'PUT' : 'POST';
		let index: number | undefined;
		if (row) {
			index = await findMappingIndex(
				row['Tipo'] as string,
				row['Valor'] as string,
				(row['Movimiento'] as string) || DEFAULT_DIRECTION
			);
			if (index === -1) return 'No se encontró el mapping para editar';
		}

		const res = await unsafeAPI(
			method,
			`${API_BASE}/bank_account_mappings/`,
			{ index, direction: values.direction, type: values.type, category: values.category, value: values.value },
			userData,
			internalCountry.get()
		);
		if (res?.success) {
			refreshTable();
			showNotice(row ? 'Mapping actualizado exitosamente' : 'Mapping creado exitosamente', 'success');
			return true;
		}
		return res?.status || (row ? 'Error al actualizar el mapping' : 'Error al crear el mapping');
	};

	const handleCreateProvider = async (values: {
		name: string;
		identification_number: string;
		account_number: string;
		category: string;
		secondary_category: string;
		manual_payment_limit_usd?: number | null;
	}): Promise<true | string> => {
		const res = await unsafeAPI(
			'POST',
			`${API_BASE}/proveedores/`,
			{
				name: values.name,
				identification_number: values.identification_number,
				account_number: values.account_number,
				category: values.category,
				secondary_category: values.secondary_category,
				...(isAdmin ? { manual_payment_limit_usd: values.manual_payment_limit_usd } : {})
			},
			userData,
			internalCountry.get()
		);
		if (res?.success) {
			refreshTable();
			showNotice(isAdmin ? 'Proveedor creado exitosamente' : 'Proveedor creado. Pendiente de aprobación.', 'success');
			return true;
		}
		return res?.detail || res?.status || 'Error al crear el proveedor';
	};

	const handleEdit = (row: { [key: string]: string | number }) => setMappingDialogRow(row);

	const handleDelete = async (row: { [key: string]: string | number }): Promise<true | string> => {
		const index = await findMappingIndex(row['Tipo'] as string, row['Valor'] as string, (row['Movimiento'] as string) || DEFAULT_DIRECTION);
		if (index === -1) return 'No se encontró el mapping para eliminar';
		const res = await unsafeAPI(
			'DELETE',
			`${API_BASE}/bank_account_mappings/`,
			{ index },
			userData,
			internalCountry.get()
		);
		if (res?.success) {
			refreshTable();
			showNotice('Mapping eliminado exitosamente', 'success');
			return true;
		}
		return res?.status || 'Error al eliminar el mapping';
	};

	const handleEditProveedor = (row: { [key: string]: string | number }) => {
		const proveedorId = String(row['ID'] || '');
		const secondaryCategory = String(row['Categoría secundaria'] || '');
		setEditDialogProps({
			title: 'Editar Proveedor',
			className: 'min-w-[min(480px,90vw)]',
			fields: [
				...(isAdmin ? [{
					type: 'input' as const,
					name: 'manual_payment_limit_usd',
					label: 'Límite por pago en USD (opcional)',
					placeholder: 'Déjalo vacío para usar el límite general',
					required: false,
					defaultValue: String(row['Límite por pago USD'] || '')
				}] : []),
				{ type: 'input', name: 'name', label: 'Nombre', defaultValue: row['Nombre'] as string },
				{ type: 'input', name: 'identification_number', label: 'RIF / Cédula', defaultValue: row['Identificación'] as string },
				{ type: 'input', name: 'account_number', label: 'Número de cuenta', defaultValue: row['Cuenta'] as string },
				{ type: 'select', name: 'category', label: 'Categoría', options: validCategories, defaultValue: row['Categoría'] as string },
				{
					type: 'select',
					name: 'secondary_category',
					label: 'Categoría secundaria (opcional)',
					options: [{ label: 'Sin categoría secundaria', value: NO_SECONDARY_CATEGORY }, ...validCategories],
					defaultValue: secondaryCategory || NO_SECONDARY_CATEGORY,
					required: false
				}
			],
			onConfirm: async (values) => {
				if (!String(values.name).trim()) return 'El nombre es requerido';
				if (!String(values.identification_number).trim()) return 'La identificación es requerida';
				if (!String(values.account_number).trim()) return 'El número de cuenta es requerido';
				if (!values.category) return 'La categoría es requerida';
				const limit = String(values.manual_payment_limit_usd || '').trim();
				if (isAdmin && limit && (!Number.isFinite(Number(limit)) || Number(limit) <= 0)) {
					return 'El límite por pago en USD debe ser un número positivo';
				}
				const nextCategory = String(values.category);
				const nextSecondaryCategory =
					values.secondary_category && values.secondary_category !== NO_SECONDARY_CATEGORY ? String(values.secondary_category) : '';
				if (nextSecondaryCategory && nextSecondaryCategory === nextCategory) {
					return 'La categoría secundaria debe ser distinta a la principal';
				}
				const res = await unsafeAPI(
					'PUT',
					`${API_BASE}/proveedores/`,
					{
						id: proveedorId,
						name: String(values.name).trim(),
						identification_number: String(values.identification_number).trim(),
						account_number: String(values.account_number).replace(/\D/g, ''),
						category: nextCategory,
						secondary_category: nextSecondaryCategory,
						...(isAdmin ? { manual_payment_limit_usd: limit ? Number(limit) : null } : {})
					},
					userData,
					internalCountry.get()
				);
				if (res?.success) {
					refreshTable();
					showNotice('Proveedor actualizado exitosamente', 'success');
					return true;
				}
				return res?.detail || res?.status || 'Error al actualizar el proveedor';
			},
			onClose: () => setEditDialogProps(null),
		});
	};

	const handleDeleteProveedor = async (row: { [key: string]: string | number }): Promise<true | string> => {
		const proveedorId = String(row['ID'] || '');
		const res = await unsafeAPI(
			'DELETE',
			`${API_BASE}/proveedores/`,
			{ id: proveedorId },
			userData,
			internalCountry.get()
		);
		if (res?.success) {
			refreshTable();
			showNotice('Proveedor eliminado exitosamente', 'success');
			return true;
		}
		return res?.detail || res?.status || 'Error al eliminar el proveedor';
	};

	if (!userData || !optionsLoaded) return <ContainerSpinner />;

	return (
		<div className="flex h-full w-full flex-col gap-4 overflow-hidden p-4 md:p-6">
			<div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
				<h1 className="text-lg font-semibold text-neutral-100">Bank Account Mappings</h1>
				<div className="flex flex-wrap items-center gap-2">
					{activeTab === 'proveedores' && isAdmin && selectedProviders.length > 0 && (
						<>
							<Button
								variant="primary"
								className="!w-fit"
								onClick={() => setApproveAction('approve')}
								Icon={<ShieldCheck className="size-4" />}
							>
								Aprobar ({selectedProviders.length})
							</Button>
							<Button
								variant="destructive"
								className="!w-fit"
								onClick={() => setApproveAction('reject')}
								Icon={<ShieldX className="size-4" />}
							>
								Rechazar ({selectedProviders.length})
							</Button>
						</>
					)}
					{activeTab === 'proveedores' && (
						<Button onClick={() => setProviderDialogOpen(true)} Icon={<UserPlus className="size-4" />}>
							Crear proveedor
						</Button>
					)}
					{activeTab === 'mappings' && (
						<Button onClick={handleCreate} Icon={<Plus className="size-4" />}>
							Crear mapping
						</Button>
					)}
				</div>
			</div>

			<TabBar tabs={PAGE_TABS} activeTab={activeTab} onTabChange={setActiveTab} />

			{notice && <Notice message={notice.message} variant={notice.variant} />}
			{mappingDialogRow && (
				<MappingDialog
					title={mappingDialogRow === 'create' ? 'Crear mapping' : 'Editar mapping'}
					initialValues={
						mappingDialogRow === 'create'
							? { direction: DEFAULT_DIRECTION }
							: {
									direction: (mappingDialogRow['Movimiento'] as string) || DEFAULT_DIRECTION,
									type: mappingDialogRow['Tipo'] as string,
									category: mappingDialogRow['Categoria'] as string,
									value: mappingDialogRow['Valor'] as string
								}
					}
					validTypes={validTypes}
					validDirections={validDirections}
					categoriesByDirection={categoriesByDirection}
					onConfirm={(values) => handleConfirmMapping(values, mappingDialogRow === 'create' ? undefined : mappingDialogRow)}
					onClose={() => setMappingDialogRow(null)}
				/>
			)}
			{editDialogProps && <ConfirmInputDialog {...editDialogProps} />}
			{providerDialogOpen && (
				<ProviderDialog
					isAdmin={isAdmin}
					onConfirm={handleCreateProvider}
					onClose={() => setProviderDialogOpen(false)}
					categories={validCategories}
				/>
			)}
			{approveAction && selectedProviders.length > 0 && (
				<ApproveProviderDialog
					providerIds={selectedProviders.map((p) => String(p['ID'] || ''))}
					action={approveAction}
					onClose={() => setApproveAction(null)}
					onActionComplete={() => {
						setApproveAction(null);
						setSelectedProviders([]);
						refreshTable();
					}}
				/>
			)}

			{activeTab === 'mappings' && (
				<div className="min-h-0 flex-1">
					<Table
						key={tableKey}
						userEmail={userData.master_store?.id}
						endpoint={'https://api.krece.app/tables/bank_account_mappings/'}
						orderBy={{ columnName: 'Tipo', columnOrder: 'ASC' }}
						pagination={{ default: 50, batchSizes: [50] }}
						hideDateFilters
						hideExport
						search_by={[
							{ column: 'Valor', placeholder: 'Buscar por cuenta o identificación...', type: 'alphanumeric', label: 'Valor' }
						]}
						filters={[
							{
								column: 'Movimiento',
								options: [{ label: 'Todos los movimientos', value: 'Todos los movimientos' }, ...validDirections],
								defaultValue: 'Todos los movimientos'
							},
							{
								column: 'Tipo',
								options: [{ label: 'Todos los tipos', value: 'Todos los tipos' }, ...validTypes],
								defaultValue: 'Todos los tipos'
							},
							{
								column: 'Categoria',
								options: [{ label: 'Todas las categorías', value: 'Todas las categorías' }, ...validMappingCategories],
								defaultValue: 'Todas las categorías'
							}
						]}
						actionButton={{ label: 'Editar', onClick: handleEdit }}
						deleteButton={{ onClick: handleDelete }}
					/>
				</div>
			)}

			{activeTab === 'proveedores' && (
				<div className="min-h-0 flex-1">
					<Table
						key={proveedoresTableKey}
						userEmail={null}
						endpoint={'https://api.krece.app/tables/proveedores/'}
						orderBy={{ columnName: 'Nombre', columnOrder: 'ASC' }}
						pagination={{ default: 50, batchSizes: [50, 100] }}
						hideDateFilters
						hideExport
						search_by={[
							{ column: 'Nombre', placeholder: 'Buscar por nombre...', label: 'Nombre' },
							{ column: 'Cuenta', placeholder: 'Buscar por número de cuenta...', type: 'numbers_only', label: 'Número de cuenta' }
						]}
						filters={[
							{
								column: 'Categoría',
								options: [{ label: 'Todas las categorías', value: 'Todas las categorías' }, ...validCategories],
								defaultValue: 'Todas las categorías'
							},
							{
								column: 'Estado',
								options: [
									{ label: 'Todos los estados', value: 'Todos los estados' },
									{ label: 'Pendiente', value: 'pending_approval' },
									{ label: 'Aprobado', value: 'approved' },
									{ label: 'Rechazado', value: 'rejected' },
								],
								defaultValue: 'Todos los estados'
							}
						]}
						actionButton={{ label: 'Editar', onClick: handleEditProveedor }}
						deleteButton={{ onClick: handleDeleteProveedor }}
						{...(isAdmin ? { selectable: { idColumn: 'ID', onSelectionChange: setSelectedProviders } } : {})}
					/>
				</div>
			)}
		</div>
	);
}
